import {
  FilterFieldLike,
  ParseUrlFiltersOptions,
  PeriodParsedLike,
  QueryParamsLike,
  QueryParamsOutput
} from '../models/query-filters-parser.model';


/* Parseia período a partir de JSON string. Retorna null se inválido. */
export function parsePeriodFromJson(value: unknown): PeriodParsedLike | null {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/* Normaliza cada valor para comparação/uso: tenta número, senão mantém string limpa. */
function toComparableScalar(value: unknown): string | number {
  const trimmed = String(value ?? '').trim();
  const parsedNumber = Number(trimmed);
  return Number.isNaN(parsedNumber) ? trimmed : parsedNumber;
}

/* Indica se uma chave deve preservar o valor literal, sem split/conversão. */
function isRawValue(key: string, value: unknown, rawKeys: string[]): boolean {
  if (!rawKeys.includes(key) || typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

/* Decodifica valor textual de query param quando necessário. */
function decodeParamValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/* Remove campos não necessários do period para serialização na URL. */
function compactPeriodForUrl(period: Record<string, unknown>): Record<string, unknown> {
  const compactPeriod: Record<string, unknown> = {};

  Object.entries(period).forEach(([entryKey, entryValue]) => {
    if (entryKey === 'customPeriod') {
      return;
    }

    if (entryValue === null || entryValue === undefined || entryValue === '') {
      return;
    }

    compactPeriod[entryKey] = entryValue;
  });

  return compactPeriod;
}

/* Compara valores de filtro/opção com normalização (trim + lowercase) para evitar mismatch de tipo/formatação. */
function matchesSelectedValue(selectedValue: unknown, optionValue: unknown): boolean {
  return String(selectedValue ?? '').trim().toLowerCase() === String(optionValue ?? '').trim().toLowerCase();
}

/* Lê query params por chaves permitidas e retorna mapa normalizado de filtros. */
export function parseUrlFiltersByKeys(
  params: QueryParamsLike,
  filterKeys: string[],
  options?: ParseUrlFiltersOptions
): Record<string, unknown> | null {
  const filters: Record<string, unknown> = {};
  const rawKeys = options?.rawKeys ?? [];
  let hasFilters = false;

  filterKeys.forEach((key) => {
    const paramValue = params[key];
    if (!paramValue) {
      return;
    }

    hasFilters = true;

    const normalizedValue = typeof paramValue === 'string' ? decodeParamValue(paramValue) : paramValue;

    if (isRawValue(key, normalizedValue, rawKeys)) {
      filters[key] = String(normalizedValue).trim();
      return;
    }

    if (typeof normalizedValue === 'string' && normalizedValue.includes(',')) {
      filters[key] = normalizedValue.split(',').map((value) => toComparableScalar(value));
      return;
    }

    filters[key] = [toComparableScalar(normalizedValue)];
  });

  return hasFilters ? filters : null;
}

/* Verifica se um valor de filtro conta como ativo (usado na URL e no contador). */
export function isFilterValueActive(key: string, value: unknown): boolean {
  if (value === null || value === undefined) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return true;
  }

  if (typeof value === 'object') {
    if (key === 'period') {
      return isPeriodValueActive(value as {
        customPeriod?: boolean;
        periodStart?: unknown;
        periodEnd?: unknown;
        fiscalYear?: unknown;
        period?: unknown;
      });
    }

    return Object.values(value).some((nestedValue) => isFilterValueActive('', nestedValue));
  }

  return false;
}

/* Regra de ativo para period: só conta quando customPeriod está ligado e há valor selecionado. */
function isPeriodValueActive(periodValue: {
  customPeriod?: boolean;
  periodStart?: unknown;
  periodEnd?: unknown;
  fiscalYear?: unknown;
  period?: unknown;
}): boolean {
  return countPeriodValueActive(periodValue) > 0;
}

/* Regra de contagem para period:
 * - customPeriod=false: conta como 1 filtro ativo quando houver um period selecionado
 * - customPeriod=true: conta como 1 filtro ativo quando houver qualquer data preenchida */
function countPeriodValueActive(periodValue: {
  customPeriod?: boolean;
  periodStart?: unknown;
  periodEnd?: unknown;
  fiscalYear?: unknown;
  period?: unknown;
}): number {
  if (!periodValue) {
    return 0;
  }

  if (!periodValue.customPeriod) {
    return [periodValue.fiscalYear, periodValue.period].some((value) => isFilterValueActive('', value)) ? 1 : 0;
  }

  return [periodValue.periodStart, periodValue.periodEnd].some((value) => isFilterValueActive('', value))
    ? 1
    : 0;
}

/* Conta quantos campos da config possuem filtro ativo. */
export function countActiveFiltersByConfig(config: { fields: FilterFieldLike[] }): number {
  let count = 0;

  config.fields.forEach((field) => {
    switch (field.type) {
      case 'chip-select':
      case 'autocomplete': {
        const activeCount = field.options?.filter((opt) => opt.selected).length ?? 0;
        if (activeCount) count++;
        break;
      }
      case 'period': {
        count += countPeriodValueActive(
          (field.value as {
            customPeriod?: boolean;
            periodStart?: unknown;
            periodEnd?: unknown;
            fiscalYear?: unknown;
            period?: unknown;
          }) ?? {}
        );
        break;
      }
      case 'select':
      case 'text':
      case 'toggle': {
        if (isFilterValueActive(field.key, field.value)) count++;
        break;
      }
    }
  });

  return count;
}

/* Aplica valores parseados da URL no config de filtros (mutação controlada do objeto recebido). */
export function applyFiltersToConfigByValues(config: { fields: FilterFieldLike[] }, filters: Record<string, unknown>): void {
  config.fields.forEach((field) => {
    if (!filters[field.key]) return;

    const value = filters[field.key];
    const valuesToSelect = Array.isArray(value) ? value : [value];

    switch (field.type) {
      case 'chip-select':
      case 'autocomplete':
        field.options?.forEach((opt) => {
          opt.selected = valuesToSelect.some((selectedValue) => matchesSelectedValue(selectedValue, opt.value));
        });
        break;

      case 'select':
        field.options?.forEach((opt) => {
          opt.selected = valuesToSelect.some((selectedValue) => matchesSelectedValue(selectedValue, opt.value));
        });
        field.value = valuesToSelect[0] ?? null;
        break;

      case 'period':
        if (typeof valuesToSelect[0] === 'string') {
          try {
            const parsed = JSON.parse(valuesToSelect[0]);
            if (parsed && typeof parsed === 'object') {
              const periodValue = parsed as PeriodParsedLike;
              const hasCustomDates = isFilterValueActive('', periodValue.periodStart) || isFilterValueActive('', periodValue.periodEnd);

              field.value = {
                customPeriod: periodValue.customPeriod ?? hasCustomDates,
                fiscalYear: periodValue.fiscalYear ?? null,
                period: periodValue.period ?? null,
                periodStart: periodValue.periodStart ?? null,
                periodEnd: periodValue.periodEnd ?? null
              };
            }
          } catch { }
        }
        break;

      case 'toggle':
        field.value = valuesToSelect[0] === 'true' || valuesToSelect[0] === true;
        break;

      case 'text':
        field.value = valuesToSelect[0];
        break;
    }
  });
}

/* Converte mapa de valores de filtro para query params da URL. */
export function buildQueryParamsFromFilterValues(values: Record<string, unknown>): QueryParamsOutput {
  const queryParams: QueryParamsOutput = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === 'period' && typeof value === 'object' && value !== null) {
      const compactPeriod = compactPeriodForUrl(value as Record<string, unknown>);
      if (Object.keys(compactPeriod).length > 0) {
        queryParams[key] = JSON.stringify(compactPeriod);
      }
      return;
    }

    if (!isFilterValueActive(key, value)) {
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      queryParams[key] = value.map((v) => String(v)).join(',');
    } else if (typeof value === 'object' && value !== null) {
      queryParams[key] = JSON.stringify(value);
    } else if (value !== null && value !== undefined && value !== '') {
      queryParams[key] = String(value);
    }
  });

  return queryParams;
}

/* Extrai o período padrão das domains (primeiro FY + primeiro Period disponível). */
export function getDefaultPeriodValue(domains: {
  periods?: {
    fiscalYears?: Array<{ FY: string }>;
    currentFy?: number;
    quarters?: string[];
    month?: string[];
  };
}): {
  customPeriod: false;
  fiscalYear: string;
  period: string;
  periodStart: null;
  periodEnd: null;
} | null {
  const fiscalYears = domains.periods?.fiscalYears ?? [];
  const currentFy = String(domains.periods?.currentFy ?? '').trim();
  const fiscalYear =
    fiscalYears.find((item) => String(item.FY ?? '').replace(/\D/g, '') === currentFy.replace(/\D/g, ''))?.FY
    ?? fiscalYears[0]?.FY
    ?? null;
  const period = 'YTD';

  if (!fiscalYear || !period) {
    return null;
  }

  return {
    customPeriod: false,
    fiscalYear,
    period,
    periodStart: null,
    periodEnd: null
  };
}

